// pages/admin/StatusMaintenance.jsx
// Admin-side control for status.kailux.co.za's scheduled maintenance AND
// incidents. Two distinct workflows live here:
//  1. Scheduled maintenance — a place to set a start time AND an estimated
//     end time (the ETA shown on the public page), which auto-emails
//     subscribers the moment it's created. See backend/main.py's
//     create_maintenance_window and scripts/status_monitor.py /
//     status-page/api/monitor.js for the rest of the pipeline (the
//     started/completed transitions and their emails run independently of
//     this backend, by design).
//  2. Incidents — both viewing the ones the monitor auto-opened, and
//     manually opening one or posting an update on any of them (moving it
//     through investigating -> identified -> monitoring -> resolved). This
//     exists because the monitor only checks two URLs every 5 minutes with
//     a 2-bad-checks debounce; there will always be problems Madembe spots
//     before, or that, the monitor never would.
import { useState, useEffect } from 'react';
import {
  getStatusComponents, listMaintenanceWindows, createMaintenanceWindow,
  cancelMaintenanceWindow, getStatusIncidents, createStatusIncident,
  addStatusIncidentUpdate, getStatusErrorEvents,
} from '../api/kailux';
import Spinner from '../components/Spinner';
import { T, alpha } from '../theme/theme'


const inp = {
  width:'100%', padding:'8px 12px', background: T.s1, border:`1px solid ${T.bd}`,
  borderRadius:'8px', color: T.tx, fontSize:'14px', outline:'none',
  fontFamily:"'Outfit',sans-serif", boxSizing:'border-box',
}

const STATUS_BADGE = {
  scheduled:   { bg: T.bld, fg: T.bl, label: 'Scheduled' },
  in_progress: { bg: T.amd, fg: T.am, label: 'In progress' },
  completed:   { bg: T.gnd, fg: T.gn, label: 'Completed' },
  cancelled:   { bg: T.s3,  fg: T.mt, label: 'Cancelled' },
}

const INCIDENT_BADGE = {
  investigating: { bg: T.rdd, fg: T.rd, label: 'Investigating' },
  identified:    { bg: T.amd, fg: T.am, label: 'Identified' },
  monitoring:    { bg: T.bld, fg: T.bl, label: 'Monitoring' },
  resolved:      { bg: T.gnd, fg: T.gn, label: 'Resolved' },
}

const INCIDENT_STATUS_OPTIONS = ['investigating', 'identified', 'monitoring', 'resolved'];
const SEVERITY_OPTIONS = ['minor', 'major', 'critical'];

function Badge({ status, map }) {
  const b = (map || STATUS_BADGE)[status] || STATUS_BADGE.scheduled;
  return (
    <span style={{ fontSize:'11px', fontWeight:600, padding:'2px 8px', borderRadius:'999px', background:b.bg, color:b.fg }}>
      {b.label}
    </span>
  );
}

function ComponentPicker({ components, selected, onToggle }) {
  return (
    <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
      {components.map(c => {
        const active = selected.includes(c.id);
        return (
          <button type="button" key={c.id} onClick={() => onToggle(c.id)}
            style={{
              padding:'5px 12px', borderRadius:'999px', fontSize:'12.5px', cursor:'pointer',
              border:`1px solid ${active ? T.or : T.bd}`,
              background: active ? T.ord : T.s1, color: active ? T.or : T.tx,
            }}>{c.name}</button>
        );
      })}
    </div>
  );
}

function IncidentUpdateForm({ incident, onPosted, showToast }) {
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(
    incident.status === 'investigating' ? 'identified' : incident.status,
  );
  const [posting, setPosting] = useState(false);

  async function handlePost(e) {
    e.preventDefault();
    if (!message.trim()) { showToast('Add a short message before posting', 'error'); return; }
    setPosting(true);
    try {
      await addStatusIncidentUpdate(incident.id, { message, status });
      setMessage('');
      showToast('Update posted — subscribers have been emailed');
      onPosted();
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Could not post update', 'error');
    } finally {
      setPosting(false);
    }
  }

  return (
    <form onSubmit={handlePost} style={{ marginTop:'10px', paddingTop:'10px', borderTop:`1px solid ${T.bd}` }}>
      <textarea style={{ ...inp, minHeight:'50px', resize:'vertical', marginBottom:'8px' }}
        value={message} onChange={e => setMessage(e.target.value)}
        placeholder="e.g. We've identified the cause and are deploying a fix now." />
      <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
        <select value={status} onChange={e => setStatus(e.target.value)}
          style={{ ...inp, width:'auto', flex:'0 0 auto', padding:'7px 10px' }}>
          {INCIDENT_STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>{INCIDENT_BADGE[s].label}</option>
          ))}
        </select>
        <button type="submit" disabled={posting} style={{
          padding:'7px 14px', borderRadius:'7px', border:'none', background:T.prg, color:T.onpri,
          fontWeight:600, fontSize:'12.5px', cursor: posting ? 'default' : 'pointer', opacity: posting ? 0.6 : 1,
        }}>{posting ? 'Posting…' : 'Post update'}</button>
      </div>
    </form>
  );
}

export default function StatusMaintenance() {
  const [components, setComponents] = useState([]);
  const [windows, setWindows] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [errorEvents, setErrorEvents] = useState({ events: [], window_minutes: 10, window_count: 0, threshold: 5 });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [creating, setCreating] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [selectedComponents, setSelectedComponents] = useState([]); // empty = all

  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [incidentTitle, setIncidentTitle] = useState('');
  const [incidentMessage, setIncidentMessage] = useState('');
  const [incidentSeverity, setIncidentSeverity] = useState('major');
  const [incidentComponents, setIncidentComponents] = useState([]);
  const [openingIncident, setOpeningIncident] = useState(false);

  function showToast(msg, type='success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  function loadAll() {
    setLoading(true);
    Promise.all([getStatusComponents(), listMaintenanceWindows(), getStatusIncidents(), getStatusErrorEvents()])
      .then(([comps, wins, incs, errs]) => {
        setComponents(comps || []);
        setWindows(wins || []);
        setIncidents((incs || []).sort((a, b) => new Date(b.started_at) - new Date(a.started_at)));
        setErrorEvents(errs || { events: [], window_minutes: 10, window_count: 0, threshold: 5 });
        setLoading(false);
      })
      .catch(() => { showToast('Could not load status data', 'error'); setLoading(false); });
  }

  useEffect(loadAll, []);

  async function handleCreate(e) {
    e.preventDefault();
    if (!title || !start || !end) {
      showToast('Title, start time, and estimated end time are all required', 'error');
      return;
    }
    if (new Date(end) <= new Date(start)) {
      showToast('Estimated end time must be after the start time', 'error');
      return;
    }
    setCreating(true);
    try {
      await createMaintenanceWindow({
        title,
        description,
        component_ids: selectedComponents.length ? selectedComponents : null,
        scheduled_start: new Date(start).toISOString(),
        scheduled_end: new Date(end).toISOString(),
      });
      showToast('Maintenance scheduled — subscribers have been emailed');
      setTitle(''); setDescription(''); setStart(''); setEnd(''); setSelectedComponents([]);
      loadAll();
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Could not schedule maintenance', 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handleCancel(id) {
    if (!window.confirm('Cancel this scheduled maintenance window? Subscribers will not be emailed about the cancellation.')) return;
    try {
      await cancelMaintenanceWindow(id);
      showToast('Maintenance window cancelled');
      loadAll();
    } catch {
      showToast('Could not cancel window', 'error');
    }
  }

  async function handleOpenIncident(e) {
    e.preventDefault();
    if (!incidentTitle.trim() || !incidentMessage.trim()) {
      showToast('Title and an opening message are both required', 'error');
      return;
    }
    setOpeningIncident(true);
    try {
      await createStatusIncident({
        title: incidentTitle, message: incidentMessage, severity: incidentSeverity,
        component_ids: incidentComponents.length ? incidentComponents : null,
      });
      showToast('Incident opened — subscribers have been emailed');
      setIncidentTitle(''); setIncidentMessage(''); setIncidentSeverity('major'); setIncidentComponents([]);
      setShowIncidentForm(false);
      loadAll();
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Could not open incident', 'error');
    } finally {
      setOpeningIncident(false);
    }
  }

  if (loading) return <div style={{ padding:'40px', textAlign:'center' }}><Spinner /></div>;

  const openIncidents = incidents.filter(i => i.status !== 'resolved');
  const resolvedIncidents = incidents.filter(i => i.status === 'resolved').slice(0, 5);

  return (
    <div style={{ maxWidth:'760px' }}>
      <h2 style={{ fontSize:'18px', fontWeight:700, color:T.tx, margin:'0 0 4px' }}>Status Page</h2>
      <p style={{ fontSize:'13px', color:T.mt, marginBottom:'20px' }}>
        The automated monitor only checks two URLs every 5 minutes and needs two bad checks in a
        row before it opens anything — you'll often notice a problem first. Use "Open incident"
        below any time that happens, and post updates to move an incident through
        investigating → identified → monitoring → resolved. Every post here emails subscribers.
      </p>

      {toast && (
        <div style={{
          padding:'10px 14px', borderRadius:'8px', marginBottom:'16px', fontSize:'13px',
          background: toast.type === 'error' ? T.rdd : T.gnd,
          color: toast.type === 'error' ? T.rd : T.gn,
        }}>{toast.msg}</div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
        <h3 style={{ fontSize:'14px', fontWeight:600, color:T.tx, margin:0 }}>Incidents</h3>
        <button onClick={() => setShowIncidentForm(s => !s)} style={{
          padding:'6px 12px', borderRadius:'7px', border:`1px solid ${alpha(T.rd, 0.33)}`, background:'transparent',
          color:T.rd, fontSize:'12.5px', fontWeight:600, cursor:'pointer',
        }}>{showIncidentForm ? 'Cancel' : '+ Open incident'}</button>
      </div>

      {showIncidentForm && (
        <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'18px', marginBottom:'16px' }}>
          <form onSubmit={handleOpenIncident}>
            <div style={{ marginBottom:'10px' }}>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'4px' }}>Title</label>
              <input style={inp} value={incidentTitle} onChange={e => setIncidentTitle(e.target.value)}
                placeholder="e.g. Bank statement uploads failing for some firms" />
            </div>
            <div style={{ marginBottom:'10px' }}>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'4px' }}>Opening message</label>
              <textarea style={{ ...inp, minHeight:'60px', resize:'vertical' }} value={incidentMessage}
                onChange={e => setIncidentMessage(e.target.value)}
                placeholder="What's happening, as you'd want a client to read it" />
            </div>
            <div style={{ display:'flex', gap:'10px', marginBottom:'10px' }}>
              <div>
                <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'4px' }}>Severity</label>
                <select value={incidentSeverity} onChange={e => setIncidentSeverity(e.target.value)} style={inp}>
                  {SEVERITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom:'14px' }}>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'4px' }}>
                Affected components (none selected = platform-wide)
              </label>
              <ComponentPicker components={components} selected={incidentComponents}
                onToggle={id => setIncidentComponents(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])} />
            </div>
            <button type="submit" disabled={openingIncident} style={{
              padding:'9px 18px', borderRadius:'8px', border:'none', background:T.rd, color:T.onpri,
              fontWeight:600, fontSize:'13.5px', cursor: openingIncident ? 'default' : 'pointer', opacity: openingIncident ? 0.6 : 1,
            }}>{openingIncident ? 'Opening…' : 'Open incident & notify subscribers'}</button>
          </form>
        </div>
      )}

      {openIncidents.length === 0 && (
        <p style={{ fontSize:'13px', color:T.mt, marginBottom:'20px' }}>No open incidents.</p>
      )}
      {openIncidents.map(inc => (
        <div key={inc.id} style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'10px', padding:'14px 16px', marginBottom:'10px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
            <span style={{ fontSize:'14px', fontWeight:600, color:T.tx }}>{inc.title}</span>
            <Badge status={inc.status} map={INCIDENT_BADGE} />
          </div>
          <p style={{ fontSize:'12px', color:T.mt, margin:'0 0 6px' }}>
            Started {new Date(inc.started_at).toLocaleString()} {inc.auto_created ? '· auto-detected' : '· opened manually'}
          </p>
          {(inc.status_incident_updates || []).slice().sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).map(u => (
            <p key={u.id} style={{ fontSize:'12.5px', color:T.tx, margin:'4px 0' }}>
              <span style={{ color:T.mt }}>{new Date(u.created_at).toLocaleTimeString()} — </span>{u.message}
            </p>
          ))}
          <IncidentUpdateForm incident={inc} onPosted={loadAll} showToast={showToast} />
        </div>
      ))}

      {resolvedIncidents.length > 0 && (
        <details style={{ marginBottom:'24px' }}>
          <summary style={{ fontSize:'12.5px', color:T.mt, cursor:'pointer', marginBottom:'8px' }}>
            Recently resolved ({resolvedIncidents.length})
          </summary>
          {resolvedIncidents.map(inc => (
            <div key={inc.id} style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'10px', padding:'10px 16px', marginBottom:'6px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <span style={{ fontSize:'13.5px', color:T.tx }}>{inc.title}</span>
                <Badge status={inc.status} map={INCIDENT_BADGE} />
              </div>
            </div>
          ))}
        </details>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
        <h3 style={{ fontSize:'14px', fontWeight:600, color:T.tx, margin:0 }}>Recent errors</h3>
        <span style={{
          fontSize:'12px', fontWeight:600,
          color: errorEvents.window_count >= errorEvents.threshold ? T.rd : T.mt,
        }}>
          {errorEvents.window_count}/{errorEvents.threshold} in last {errorEvents.window_minutes} min
        </span>
      </div>
      <p style={{ fontSize:'12.5px', color:T.mt, marginBottom:'12px' }}>
        Every one of these also emailed admin@kailux.co.za immediately — this list is here so you
        can see them on the dashboard too, not just in your inbox. Individually they don't affect
        the public status page; {errorEvents.threshold}+ within {errorEvents.window_minutes} minutes opens a public incident automatically.
        Errors tagged <strong style={{ color:T.or }}>TEST</strong> (from the trigger-test-error endpoint) never count toward that threshold.
      </p>
      {errorEvents.events.length === 0 && (
        <p style={{ fontSize:'13px', color:T.mt, marginBottom:'20px' }}>No errors logged yet.</p>
      )}
      {errorEvents.events.length > 0 && (
        <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'10px', padding:'4px 16px', marginBottom:'24px' }}>
          {errorEvents.events.map(ev => (
            <div key={ev.id} style={{
              display:'flex', justifyContent:'space-between', alignItems:'center',
              padding:'10px 0', borderBottom:`1px solid ${T.bd}`,
            }}>
              <div>
                <span style={{ fontSize:'13px', color:T.tx, fontWeight:600 }}>{ev.error_type}</span>
                {ev.is_test && (
                  <span style={{
                    fontSize:'10px', fontWeight:700, color:T.or, background:T.ord,
                    padding:'1px 6px', borderRadius:'999px', marginLeft:'8px',
                  }}>TEST</span>
                )}
                <span style={{ fontSize:'12px', color:T.mt, marginLeft:'8px' }}>{ev.method} {ev.path}</span>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:'11.5px', color:T.mt, fontFamily:'monospace' }}>{ev.error_id}</div>
                <div style={{ fontSize:'11px', color:T.mt }}>{new Date(ev.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'18px', marginBottom:'24px' }}>
        <h3 style={{ fontSize:'14px', fontWeight:600, color:T.tx, margin:'0 0 12px' }}>Schedule new maintenance</h3>
        <form onSubmit={handleCreate}>
          <div style={{ marginBottom:'10px' }}>
            <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'4px' }}>Title</label>
            <input style={inp} value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Database upgrade" />
          </div>
          <div style={{ marginBottom:'10px' }}>
            <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'4px' }}>Description</label>
            <textarea style={{ ...inp, minHeight:'60px', resize:'vertical' }} value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What's happening and what users should expect" />
          </div>
          <div style={{ display:'flex', gap:'10px', marginBottom:'10px' }}>
            <div style={{ flex:1 }}>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'4px' }}>Starts</label>
              <input style={inp} type="datetime-local" value={start} onChange={e => setStart(e.target.value)} />
            </div>
            <div style={{ flex:1 }}>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'4px' }}>Estimated end (ETA)</label>
              <input style={inp} type="datetime-local" value={end} onChange={e => setEnd(e.target.value)} />
            </div>
          </div>
          <div style={{ marginBottom:'14px' }}>
            <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'4px' }}>
              Affected components (none selected = all)
            </label>
            <ComponentPicker components={components} selected={selectedComponents}
              onToggle={id => setSelectedComponents(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])} />
          </div>
          <button type="submit" disabled={creating} style={{
            padding:'9px 18px', borderRadius:'8px', border:'none', background:T.prg, color:T.onpri,
            fontWeight:600, fontSize:'13.5px', cursor: creating ? 'default' : 'pointer', opacity: creating ? 0.6 : 1,
          }}>
            {creating ? 'Scheduling…' : 'Schedule & notify subscribers'}
          </button>
        </form>
      </div>

      <h3 style={{ fontSize:'14px', fontWeight:600, color:T.tx, margin:'0 0 10px' }}>All maintenance windows</h3>
      {windows.length === 0 && <p style={{ fontSize:'13px', color:T.mt }}>No maintenance windows yet.</p>}
      {windows.map(w => (
        <div key={w.id} style={{
          background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'10px', padding:'12px 16px', marginBottom:'8px',
          display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'12px',
        }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
              <span style={{ fontSize:'14px', fontWeight:600, color:T.tx }}>{w.title}</span>
              <Badge status={w.status} />
            </div>
            <p style={{ fontSize:'12px', color:T.mt, margin:0 }}>
              {new Date(w.scheduled_start).toLocaleString()} → {new Date(w.scheduled_end).toLocaleString()}
            </p>
          </div>
          {(w.status === 'scheduled') && (
            <button onClick={() => handleCancel(w.id)} style={{
              padding:'5px 10px', borderRadius:'6px', border:`1px solid ${alpha(T.rd, 0.33)}`, background:'transparent',
              color:T.rd, fontSize:'12px', cursor:'pointer', flexShrink:0,
            }}>Cancel</button>
          )}
        </div>
      ))}
    </div>
  );
}
